/**
 * A qué organización se entra: la que se elige al entrar, la que se retoma al
 * volver, y qué estado tiene la sesión mientras todavía no hay ninguna.
 *
 * Son tres preguntas y comparten una regla, que es la razón de que vivan juntas
 * y aparte de las pantallas: **nunca se entra a una organización que nadie
 * eligió**. Los datos de dos organizaciones no se parecen en nada más que en el
 * formato, y aterrizar en la equivocada no se nota — se nota tres pantallas
 * después, cuando alguien lee un número que no es el suyo.
 *
 * ENTRAR Y ELEGIR SON DOS PASOS. El `POST /api/auth/login` no lleva
 * organización: la cookie que emite no pertenece a ninguna, y `GET /api/v2/tenants`
 * contesta con las de quien pregunta. O sea que autenticarse primero y elegir
 * después es lo que el servidor ya hacía; lo que faltaba era que la interfaz lo
 * dijera así, en vez de meter un desplegable en la pantalla de login que sólo
 * se podía llenar después del primer intento.
 *
 * Módulo PURO: sin React y sin red. Lo que decide se prueba con una tabla.
 */

import type { Tenant } from '@/types';

/**
 * En qué estado está la sesión de este navegador.
 *
 * `choosing` es el que se agregó, y es un estado y no un error: hay una sesión
 * viva, con sus organizaciones ya contestadas, y todavía ninguna elegida. Antes
 * ese caso caía en `anonymous` —la aplicación mandaba al login **con la sesión
 * abierta**— o, peor, lo tapaba entrando a una organización cualquiera.
 */
export type SessionStatus = 'loading' | 'anonymous' | 'choosing' | 'authenticated' | 'error';

/**
 * La organización que se retoma al volver, dado lo que este navegador recordaba.
 *
 * DOS CASOS Y UN DESCARTE:
 *
 * 1. Lo recordado sigue estando entre las suyas: ésa. Es el caso de siempre.
 * 2. Tiene una sola: ésa, sin preguntar. No hay elección que hacer, y pedirla
 *    sería un trámite — es el 24 de cada 25 y no cambió nada para él.
 * 3. Cualquier otra cosa —dos o más y lo recordado ya no vale, o nunca hubo
 *    nada recordado—: `null`, que significa «hay que elegir». La pantalla que
 *    contesta eso es el selector de organización, no el login.
 *
 * ANTES CAÍA A `user.tenantId`, la que el servidor había resuelto para la
 * sesión, y ahí estaba el agujero que este cambio cierra: con dos o más
 * organizaciones eso es entrar a la primera membresía del documento sin que
 * nadie la eligiera, exactamente lo que el desplegable del login existía para
 * impedir. El servidor sigue resolviendo una —tiene que devolver un usuario
 * dentro de alguna—, pero esa resolución ya no decide a dónde entra nadie.
 */
export function resolveActiveTenant(
  tenants: readonly Tenant[],
  stored: string | null,
): string | null {
  const known = (id: string | null | undefined): string | null =>
    id ? (tenants.find((tenant) => tenant.id === id)?.id ?? null) : null;
  const recordada = known(stored);
  if (recordada) return recordada;
  return tenants.length === 1 ? (tenants[0]?.id ?? null) : null;
}

/**
 * Si esta sesión está viva y todavía le falta elegir organización.
 *
 * Se pregunta con la lista YA contestada: sin sesión no hay lista, y con lista
 * de una la de arriba ya resolvió. Así que esto es cierto exactamente cuando
 * hay dos o más y ninguna elegida — que es cuando corresponde el selector.
 */
export const mustChooseTenant = (tenants: readonly Tenant[], active: string | null): boolean =>
  tenants.length > 1 && active === null;

/**
 * El estado de la sesión, dicho en un solo lugar.
 *
 * EL ORDEN IMPORTA y no es alfabético:
 *
 * 1. **Falló** — un error de red o del servidor manda sobre todo lo demás; lo
 *    que sigue hablaría de datos que no llegaron.
 * 2. **Tiene permisos resueltos** — organización activa y usuario: está adentro.
 * 3. **Le falta elegir** antes que `loading`, y ésa es la línea que hace que
 *    recargar el selector no rebote al login: la respuesta ya llegó, sólo falta
 *    la elección.
 * 4. **La respuesta viene en camino** — la primera carga, y también la que
 *    dispara elegir (la clave de la consulta cambia y se vuelve a preguntar).
 * 5. **No hay nadie**: y recién acá, el login.
 */
export function sessionStatus({
  failed,
  pending,
  entitled,
  tenants,
  active,
}: {
  failed: boolean;
  pending: boolean;
  /** Si hay organización activa Y usuario, o sea permisos que dibujar. */
  entitled: boolean;
  tenants: readonly Tenant[];
  active: string | null;
}): SessionStatus {
  if (failed) return 'error';
  if (entitled) return 'authenticated';
  if (mustChooseTenant(tenants, active)) return 'choosing';
  return pending ? 'loading' : 'anonymous';
}

/**
 * Si hay que reescribir lo que el navegador recordaba.
 *
 * Se separa de `resolveActiveTenant` porque son dos cosas: cuál vale ahora, y
 * si además hay que dejarlo anotado. Sin lo segundo, la sesión funciona pero el
 * navegador sigue guardando una organización muerta, y la próxima visita paga
 * el mismo rodeo — y cualquier pantalla que lea la elección directamente sigue
 * leyendo la vieja.
 *
 * No se reescribe con `null`: quedarse sin organización activa es lo que ya
 * hace el cierre de sesión, y borrar acá pisaría la elección de alguien
 * mientras una respuesta va en camino.
 */
export const needsTenantSync = (stored: string | null, active: string | null): boolean =>
  active !== null && active !== stored;
